/**
 * Endpoints de la categoría **Pricing** (`/api/pricing/*`).
 *
 * Los precios los sirve el servicio de *scraping* (a través de la API) y el
 * seguimiento de la competencia y la configuración salen de la base. Las
 * consultas que hablan con el servicio externo se piden con `staleTime` corto
 * porque el bot puede estar actualizando datos.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '@/lib/http/api-client';

import type {
  ComparacionPricing,
  CompetenciaPricing,
  ConfiguracionPricingEntrada,
  ConfiguracionPricingRespuesta,
  DashboardPricing,
  EstadisticasPricing,
  FiltrosEstadisticas,
  FiltrosPricing,
  PreciosPricingRespuesta,
  ProgresoPricing,
  ResultadosTareaPricing,
  TareasPricingRespuesta,
  VerificacionPricing,
} from './pricing.types';

/** Ubicación de una comparación (departamento, provincia y distrito). */
export interface UbicacionPricing {
  departamento: string;
  provincia: string;
  distrito: string;
}

const CLAVE = 'pricing';

export const pricingKeys = {
  dashboard: [CLAVE, 'dashboard'] as const,
  estadisticas: (filtros: FiltrosEstadisticas) => [CLAVE, 'estadisticas', filtros] as const,
  precios: (filtros: FiltrosPricing) => [CLAVE, 'precios', filtros] as const,
  comparacion: (ubicacion: UbicacionPricing) => [CLAVE, 'comparar', ubicacion] as const,
  competencia: [CLAVE, 'competencia'] as const,
  tareas: (limit: number) => [CLAVE, 'tareas', limit] as const,
  resultados: (taskId: string) => [CLAVE, 'resultados', taskId] as const,
  progreso: [CLAVE, 'progreso'] as const,
  configuracion: [CLAVE, 'configuracion'] as const,
};

/** Cadena de consulta de los filtros (zona, periodo, precio…). */
function cadenaDe(filtros: object): string {
  const parametros = new URLSearchParams();

  for (const [clave, valor] of Object.entries(filtros)) {
    if (valor) parametros.set(clave, String(valor));
  }

  const cadena = parametros.toString();

  return cadena ? `?${cadena}` : '';
}

export const pricingApi = {
  dashboard: (signal?: AbortSignal): Promise<DashboardPricing> =>
    apiClient.get<DashboardPricing>('/api/pricing/dashboard', { signal }),

  estadisticas: (
    filtros: FiltrosEstadisticas = {},
    signal?: AbortSignal,
  ): Promise<{ estadisticas: EstadisticasPricing }> =>
    apiClient.get<{ estadisticas: EstadisticasPricing }>(
      `/api/pricing/estadisticas${cadenaDe(filtros)}`,
      { signal },
    ),

  precios: (filtros: FiltrosPricing, signal?: AbortSignal): Promise<PreciosPricingRespuesta> =>
    apiClient.get<PreciosPricingRespuesta>(`/api/pricing/precios${cadenaDe(filtros)}`, { signal }),

  comparar: (ubicacion: UbicacionPricing, signal?: AbortSignal): Promise<ComparacionPricing> =>
    apiClient.get<ComparacionPricing>(`/api/pricing/comparar${cadenaDe(ubicacion)}`, { signal }),

  competencia: (signal?: AbortSignal): Promise<CompetenciaPricing> =>
    apiClient.get<CompetenciaPricing>('/api/pricing/competidores', { signal }),

  guardarCompetidor: (datos: {
    estacion: string;
    producto: string;
    departamento?: string;
    provincia?: string;
    distrito?: string;
    direccion?: string;
    ultimo_precio?: number;
  }): Promise<{ id: number; actualizado: boolean; mensaje: string }> =>
    apiClient.post<{ id: number; actualizado: boolean; mensaje: string }>(
      '/api/pricing/competidores',
      { body: datos },
    ),

  guardarCompetidorEstacion: (datos: {
    estacion: string;
    departamento?: string;
    provincia?: string;
    distrito?: string;
  }): Promise<{
    estacion: string;
    productos: number;
    creados: number;
    actualizados: number;
    fecha: string;
    mensaje: string;
  }> =>
    apiClient.post<{
      estacion: string;
      productos: number;
      creados: number;
      actualizados: number;
      fecha: string;
      mensaje: string;
    }>('/api/pricing/competidores/estacion', { body: datos }),

  eliminarCompetidorEstacion: (
    estacion: string,
  ): Promise<{ estacion: string; eliminados: number; mensaje: string }> =>
    apiClient.delete<{ estacion: string; eliminados: number; mensaje: string }>(
      `/api/pricing/competidores/estacion?estacion=${encodeURIComponent(estacion)}`,
    ),

  eliminarCompetidor: (id: number): Promise<{ id: number; mensaje: string }> =>
    apiClient.delete<{ id: number; mensaje: string }>(`/api/pricing/competidores/${id}`),

  verificarCompetidores: (ubicacion: UbicacionPricing): Promise<VerificacionPricing> =>
    apiClient.post<VerificacionPricing>('/api/pricing/competidores/verificar', {
      body: ubicacion,
    }),

  marcarAlerta: (id: number | 'todas'): Promise<{ id?: number; leida?: boolean }> =>
    apiClient.patch<{ id?: number; leida?: boolean }>(`/api/pricing/alertas/${id}`, {
      body: {},
    }),

  tareas: (limit = 10, signal?: AbortSignal): Promise<TareasPricingRespuesta> =>
    apiClient.get<TareasPricingRespuesta>(`/api/pricing/tareas?limit=${limit}`, { signal }),

  progreso: (signal?: AbortSignal): Promise<ProgresoPricing> =>
    apiClient.get<ProgresoPricing>('/api/pricing/scraping/progreso', { signal }),

  iniciarScraping: (datos: {
    departamento?: string;
    provincia?: string;
    distrito?: string;
    producto?: string;
    update?: boolean;
  }): Promise<{ resultado: unknown }> =>
    apiClient.post<{ resultado: unknown }>('/api/pricing/scraping', { body: datos }),

  cancelarTarea: (
    taskId: string,
    archivo?: string,
  ): Promise<{ task_id: string; mensaje: string }> =>
    apiClient.delete<{ task_id: string; mensaje: string }>(
      `/api/pricing/tareas/${encodeURIComponent(taskId)}${archivo ? `?archivo=${encodeURIComponent(archivo)}` : ''}`,
    ),

  /** Ruta (autenticada) del Excel con los resultados de una tarea. */
  exportarTarea: (taskId: string): string =>
    `/api/pricing/tareas/${encodeURIComponent(taskId)}/exportar`,

  /** Precios obtenidos por una tarea completada (detalle en modal). */
  resultadosTarea: (taskId: string, signal?: AbortSignal): Promise<ResultadosTareaPricing> =>
    apiClient.get<ResultadosTareaPricing>(
      `/api/pricing/tareas/${encodeURIComponent(taskId)}/resultados`,
      { signal },
    ),

  configuracion: (signal?: AbortSignal): Promise<ConfiguracionPricingRespuesta> =>
    apiClient.get<ConfiguracionPricingRespuesta>('/api/pricing/configuracion', { signal }),

  guardarConfiguracion: (
    datos: ConfiguracionPricingEntrada,
  ): Promise<{ id: number; mensaje: string }> =>
    apiClient.post<{ id: number; mensaje: string }>('/api/pricing/configuracion', {
      body: datos,
    }),

  cambiarEstadoConfiguracion: (
    id: number,
    activo: boolean,
  ): Promise<{ id: number; activo: boolean; mensaje: string }> =>
    apiClient.patch<{ id: number; activo: boolean; mensaje: string }>(
      `/api/pricing/configuracion/${id}/estado`,
      { body: { activo: activo ? 1 : 0 } },
    ),

  eliminarConfiguracion: (id: number): Promise<{ id: number; mensaje: string }> =>
    apiClient.delete<{ id: number; mensaje: string }>(`/api/pricing/configuracion/${id}`),
};

/** Estado del servicio, estadísticas y ficheros (dashboard). */
export function usePricingDashboard() {
  return useQuery({
    queryKey: pricingKeys.dashboard,
    queryFn: ({ signal }) => pricingApi.dashboard(signal),
    staleTime: 30_000,
  });
}

/** Estadísticas consolidadas del histórico. */
export function usePricingEstadisticas(filtros: FiltrosEstadisticas = {}) {
  return useQuery({
    queryKey: pricingKeys.estadisticas(filtros),
    queryFn: ({ signal }) => pricingApi.estadisticas(filtros, signal),
    staleTime: 60_000,
    // Al cambiar de zona se mantiene lo anterior mientras llega lo nuevo, para
    // que la pantalla no parpadee.
    placeholderData: (previo) => previo,
  });
}

/** Precios con filtros (ubicación y fecha). */
export function usePreciosPricing(filtros: FiltrosPricing, habilitada = true) {
  return useQuery({
    queryKey: pricingKeys.precios(filtros),
    queryFn: ({ signal }) => pricingApi.precios(filtros, signal),
    enabled: habilitada,
    staleTime: 30_000,
  });
}

/** Detector de cambios de una ubicación. */
export function useCompararPricing(ubicacion: UbicacionPricing, habilitada = true) {
  return useQuery({
    queryKey: pricingKeys.comparacion(ubicacion),
    queryFn: ({ signal }) => pricingApi.comparar(ubicacion, signal),
    enabled: habilitada && Boolean(ubicacion.departamento && ubicacion.provincia && ubicacion.distrito),
    staleTime: 30_000,
  });
}

/** Competidores en seguimiento y sus alertas. */
export function useCompetenciaPricing() {
  return useQuery({
    queryKey: pricingKeys.competencia,
    queryFn: ({ signal }) => pricingApi.competencia(signal),
    staleTime: 15_000,
  });
}

/** Tareas de *scraping* del servicio. */
export function useTareasPricing(limit = 10) {
  return useQuery({
    queryKey: pricingKeys.tareas(limit),
    queryFn: ({ signal }) => pricingApi.tareas(limit, signal),
    staleTime: 15_000,
  });
}

/**
 * Progreso del sistema de *scraping*.
 *
 * Se refresca solo mientras hay tareas pendientes o en ejecución, y deja de
 * consultar cuando la cola está vacía.
 */
export function useProgresoPricing() {
  return useQuery({
    queryKey: pricingKeys.progreso,
    queryFn: ({ signal }) => pricingApi.progreso(signal),
    refetchInterval: (consulta) => {
      const datos = consulta.state.data as ProgresoPricing | undefined;
      const enCurso = (datos?.stats.pending ?? 0) + (datos?.stats.running ?? 0);

      return enCurso > 0 ? 5_000 : false;
    },
  });
}

/** Configuración de las consultas programadas. */
export function useConfiguracionPricing() {
  return useQuery({
    queryKey: pricingKeys.configuracion,
    queryFn: ({ signal }) => pricingApi.configuracion(signal),
    staleTime: 30_000,
  });
}

/** Marca un competidor (o lo actualiza si ya estaba). */
export function useGuardarCompetidor() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: pricingApi.guardarCompetidor,
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: [CLAVE, 'competencia'] });
    },
  });
}

/** Marca una estación completa (todos sus productos) como competencia. */
export function useGuardarCompetidorEstacion() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: pricingApi.guardarCompetidorEstacion,
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: [CLAVE, 'competencia'] });
    },
  });
}

/** Quita una estación completa del seguimiento. */
export function useEliminarCompetidorEstacion() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: pricingApi.eliminarCompetidorEstacion,
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: [CLAVE, 'competencia'] });
    },
  });
}

/** Quita un competidor del seguimiento. */
export function useEliminarCompetidor() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: pricingApi.eliminarCompetidor,
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: [CLAVE, 'competencia'] });
    },
  });
}

/** Verifica los precios de los competidores con la última fecha disponible. */
export function useVerificarCompetidores() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: pricingApi.verificarCompetidores,
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: [CLAVE, 'competencia'] });
    },
  });
}

/** Marca una alerta como leída (o todas). */
export function useMarcarAlerta() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: pricingApi.marcarAlerta,
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: [CLAVE, 'competencia'] });
    },
  });
}

/** Lanza un *scraping* bajo demanda. */
export function useIniciarScraping() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: pricingApi.iniciarScraping,
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: [CLAVE, 'progreso'] });
      void cliente.invalidateQueries({ queryKey: [CLAVE, 'tareas'] });
    },
  });
}

/** Cancela una tarea (y su archivo, si se indica). */
export function useCancelarTarea() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: ({ taskId, archivo }: { taskId: string; archivo?: string }) =>
      pricingApi.cancelarTarea(taskId, archivo),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: [CLAVE, 'progreso'] });
      void cliente.invalidateQueries({ queryKey: [CLAVE, 'tareas'] });
      void cliente.invalidateQueries({ queryKey: [CLAVE, 'dashboard'] });
    },
  });
}

/** Resultados (precios) de una tarea completada; sólo se pide al abrir el detalle. */
export function useResultadosTarea(taskId: string | null) {
  return useQuery({
    queryKey: pricingKeys.resultados(taskId ?? 'sin-tarea'),
    queryFn: ({ signal }) => pricingApi.resultadosTarea(taskId ?? '', signal),
    enabled: Boolean(taskId),
    staleTime: 60_000,
  });
}

/** Guarda (alta o edición) una configuración de consulta programada. */
export function useGuardarConfiguracionPricing() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: pricingApi.guardarConfiguracion,
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: [CLAVE, 'configuracion'] });
    },
  });
}

/** Activa o desactiva una configuración. */
export function useCambiarEstadoConfiguracion() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: ({ id, activo }: { id: number; activo: boolean }) =>
      pricingApi.cambiarEstadoConfiguracion(id, activo),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: [CLAVE, 'configuracion'] });
    },
  });
}

/** Elimina una configuración. */
export function useEliminarConfiguracion() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: pricingApi.eliminarConfiguracion,
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: [CLAVE, 'configuracion'] });
    },
  });
}
